/* LOCAL-ONLY candidate. Not loaded by the existing preview or uploaded to Shopify.
 * Frontend verification is a UX check, NOT a server-side wholesale/discount guard.
 * Shopify cart/add.js is not guaranteed transactional, even for one items array.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.PL8WholesaleCart = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const TIERS = Object.freeze([10, 20, 30, 40, 50]);
  const PRODUCT_ID = '10344064680153';
  const QA_PRODUCT_ID = '10343873642713';
  const MARKER = 'pl8-wholesale-v1';
  const JPY_SCALE = 100;

  class CartAdapterError extends Error {
    constructor(code, message, details = {}) {
      super(message);
      this.name = 'CartAdapterError';
      this.code = code;
      this.details = details;
    }
  }
  function fail(code, message, details) { throw new CartAdapterError(code, message, details); }
  function object(value) { return value !== null && typeof value === 'object' && !Array.isArray(value); }
  function amount(value) { return Number.isSafeInteger(value) && value >= 0; }
  function id(value) {
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const text = String(value);
    return /^[1-9][0-9]*$/.test(text) && Number.isSafeInteger(Number(text)) ? text : null;
  }
  function freeze(value) {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
      Object.values(value).forEach(freeze);
      Object.freeze(value);
    }
    return value;
  }
  function validateCatalog(catalog) {
    if (!Array.isArray(catalog) || catalog.length < 1 || catalog.length > 2048) fail('INVALID_CATALOG', 'A nonempty verified design catalog is required.');
    const variants = new Set(), skus = new Set();
    return freeze(catalog.map(record => {
      const variantId = object(record) && id(record.variantId);
      if (!variantId || record.verified !== true ||
          record.wholesaleVariantId !== 'gid://shopify/ProductVariant/' + variantId ||
          record.wholesaleProductId !== 'gid://shopify/Product/' + PRODUCT_ID ||
          typeof record.retailSku !== 'string' || !/^[A-Z0-9]+(?:-[A-Z0-9]+)+$/.test(record.retailSku) ||
          record.wholesaleSku !== 'WS-' + record.retailSku ||
          record.retailVariantId === record.wholesaleVariantId || variants.has(variantId) || skus.has(record.wholesaleSku)) {
        fail('INVALID_CATALOG', 'The catalog variant/SKU mapping is invalid or duplicated.');
      }
      variants.add(variantId); skus.add(record.wholesaleSku);
      return { variantId, wholesaleSku: record.wholesaleSku, retailSku: record.retailSku };
    }));
  }
  // Deliberately separate fixture entry point; never weakens production catalog checks.
  function validateQaCatalog(catalog) {
    if (!Array.isArray(catalog) || catalog.length < 1 || catalog.length > 2) fail('INVALID_QA_CATALOG', 'Use one or two explicit QA fixture variants.');
    const variants = new Set(), skus = new Set(), printingSkus = new Set();
    return freeze(catalog.map(record => {
      const variantId = object(record) && id(record.variantId);
      if (!variantId || id(record.productId) !== QA_PRODUCT_ID || record.qaFixture !== true ||
          !/^QA-[A-Z0-9-]+$/.test(record.wholesaleSku || '') || !/^QA-PRINT-[A-Z0-9-]+$/.test(record.printingSku || '') ||
          variants.has(variantId) || skus.has(record.wholesaleSku) || printingSkus.has(record.printingSku)) {
        fail('INVALID_QA_CATALOG', 'Use the designated QA product and explicitly labeled, unique QA SKU mappings.');
      }
      variants.add(variantId); skus.add(record.wholesaleSku); printingSkus.add(record.printingSku);
      return { variantId, wholesaleSku: record.wholesaleSku, retailSku: record.printingSku };
    }));
  }
  function quantity(raw) {
    if (raw !== null && raw !== undefined && typeof raw !== 'number' && typeof raw !== 'string') return null;
    const text = String(raw ?? '').trim();
    if (text === '') return 0;
    if (!/^[0-9]+$/.test(text)) return null;
    const value = Number(text);
    return amount(value) && value <= 50 ? value : null;
  }
  function buildFromValidated(catalog, input, requestId, productId = PRODUCT_ID, qaFixture = false) {
    if (!object(input) || !Array.isArray(input.selections) || input.selections.length > catalog.length) fail('INVALID_SELECTION', 'Select designs from the verified catalog.');
    if (typeof input.businessName !== 'string' || !input.businessName.trim() || input.businessName.trim().length > 200 || /[\u0000-\u001f\u007f]/.test(input.businessName)) {
      fail('BUSINESS_REQUIRED', 'Enter a business/store name of 1–200 characters without control characters.');
    }
    if (input.businessAccepted !== true) fail('DECLARATION_REQUIRED', 'Confirm the business-purchase and wholesale-terms declaration.');
    if (typeof requestId !== 'string' || !/^[A-Za-z0-9_-]{8,100}$/.test(requestId)) fail('INVALID_REQUEST_ID', 'A unique request identifier is required.');
    const catalogMap = new Map(catalog.map(record => [record.variantId, record]));
    const seen = new Set(), lines = [];
    let totalQuantity = 0;
    input.selections.forEach(selection => {
      const variantId = object(selection) && id(selection.variantId);
      const record = catalogMap.get(variantId);
      if (!record || seen.has(variantId) || selection.wholesaleSku !== record.wholesaleSku || selection.retailSku !== record.retailSku) {
        fail('INVALID_SELECTION', 'A variant/SKU is unknown, mismatched, or duplicated.');
      }
      seen.add(variantId);
      const count = quantity(selection.quantity);
      if (count === null) fail('INVALID_QUANTITY', 'Each design quantity must be a whole number from 0 to 50.');
      totalQuantity += count;
      if (count) lines.push({ ...record, quantity: count });
    });
    if (!TIERS.includes(totalQuantity)) fail('INVALID_TOTAL', 'Choose exactly 10, 20, 30, 40, or 50 pieces in total.');
    const unitPriceJpy = totalQuantity < 30 ? 2086 : totalQuantity < 50 ? 1937 : 1788;
    const businessName = input.businessName.trim();
    const items = lines.map(line => ({
      id: Number(line.variantId), quantity: line.quantity,
      properties: {
        printingSku: line.retailSku,
        businessName,
        _pl8_wholesale: qaFixture ? 'pl8-wholesale-qa-v1' : MARKER,
        _pl8_business_declared: 'true',
        _pl8_request_id: requestId
      }
    }));
    // Only IDs, quantities, and fulfillment metadata go to Shopify. Never a price.
    return freeze({
      requestId, businessName, productId, qaFixture, lines, payload: { items },
      quote: { currency: 'JPY', quantity: totalQuantity, unitPriceJpy, subtotalJpy: unitPriceJpy * totalQuantity, expectedCartAmount: unitPriceJpy * totalQuantity * JPY_SCALE }
    });
  }
  function buildRequest(catalog, input, requestId) {
    return buildFromValidated(validateCatalog(catalog), input, requestId);
  }
  function cartEnvelope(cart) {
    if (!object(cart) || !Array.isArray(cart.items) || !amount(cart.item_count)) fail('INVALID_CART', 'Shopify returned an unreadable cart.');
    let sum = 0;
    cart.items.forEach(line => {
      if (!object(line) || !amount(line.quantity) || line.quantity === 0) fail('INVALID_CART', 'Shopify returned an invalid cart quantity.');
      sum += line.quantity;
    });
    if (!amount(sum) || sum !== cart.item_count) fail('INVALID_CART', 'Cart item counts do not agree.');
    if (cart.currency !== 'JPY') fail('CURRENCY_MISMATCH', 'This wholesale quote requires a JPY cart.');
    if (cart.taxes_included === false) fail('TAX_MODE_MISMATCH', 'The cart does not use the required tax-inclusive pricing.');
  }
  function verifyCart(cart, request) {
    cartEnvelope(cart);
    if (cart.item_count !== request.quote.quantity || cart.items.length !== request.lines.length) {
      fail('CART_MISMATCH', 'Cart quantities or line count differ from the reviewed selection.');
    }
    const expected = new Map(request.lines.map((line, index) => [line.variantId, { line, properties: request.payload.items[index].properties }]));
    const seen = new Set();
    let lineSubtotal = 0;
    cart.items.forEach(line => {
      const variantId = id(line.variant_id);
      const match = expected.get(variantId);
      if (!match || seen.has(variantId) || id(line.id) !== variantId || id(line.product_id) !== request.productId || line.sku !== match.line.wholesaleSku || line.quantity !== match.line.quantity || line.selling_plan_allocation || line.parent_relationship || line.has_components === true) {
        fail('CART_MISMATCH', 'The cart contains unexpected, duplicate, changed, or subscription items.');
      }
      seen.add(variantId);
      if (!object(line.properties) || Object.keys(line.properties).length !== Object.keys(match.properties).length || Object.entries(match.properties).some(([key, value]) => line.properties[key] !== value)) {
        fail('METADATA_MISMATCH', 'The cart printing SKU, business declaration, or request metadata does not match.');
      }
      if (!amount(line.final_line_price)) fail('INVALID_CART_PRICE', 'A server line subtotal is unavailable.');
      lineSubtotal += line.final_line_price;
    });
    if (!amount(cart.total_price) || !amount(cart.items_subtotal_price) || !amount(lineSubtotal) || lineSubtotal !== cart.items_subtotal_price || !Array.isArray(cart.cart_level_discount_applications)) {
      fail('INVALID_CART_PRICE', 'The server cart subtotal is unavailable or inconsistent.');
    }
    let cartDiscount = 0;
    cart.cart_level_discount_applications.forEach(discount => {
      if (!object(discount) || !amount(discount.total_allocated_amount)) fail('INVALID_CART_PRICE', 'A server discount allocation is unavailable.');
      cartDiscount += discount.total_allocated_amount;
    });
    if (!amount(cartDiscount) || lineSubtotal - cartDiscount !== cart.total_price) fail('INVALID_CART_PRICE', 'Server line, discount, and cart totals do not agree.');
    // total_price includes cart-level discounts; items_subtotal_price alone does not.
    if (cart.total_price !== request.quote.expectedCartAmount) {
      fail('PRICE_MISMATCH', 'The Shopify merchandise subtotal differs from the quoted wholesale total. Checkout remains disabled.', { expected: request.quote.expectedCartAmount, actual: cart.total_price, currency: 'JPY' });
    }
    return true;
  }
  function localeRoot(value) {
    if (typeof value !== 'string' || !/^\/(?:[A-Za-z0-9_-]+\/)*$/.test(value)) fail('INVALID_ROUTE', 'Use the same-origin window.Shopify.routes.root value, including its trailing slash.');
    return value;
  }
  function makeRequestId() {
    if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
    if (typeof globalThis.crypto?.getRandomValues === 'function') {
      return Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
    }
    fail('REQUEST_ID_UNAVAILABLE', 'This browser cannot generate a request identifier.');
  }
  function createAdapterWithCatalog(options, catalog, productId, qaFixture) {
    const root = localeRoot(options.routesRoot);
    const enabled = options.enabled === true;
    const fetchImpl = options.fetchImpl;
    const timeoutMs = options.timeoutMs ?? 10000;
    const requestIdFactory = options.requestIdFactory ?? makeRequestId;
    if (enabled && typeof fetchImpl !== 'function') fail('INVALID_CONFIG', 'Explicitly provide the storefront fetch implementation.');
    if (!Number.isSafeInteger(timeoutMs) || timeoutMs < 1 || typeof requestIdFactory !== 'function') fail('INVALID_CONFIG', 'Invalid timeout or request-ID configuration.');
    let busy = false, state = 'idle', attempted = null, postError = null;

    async function requestJson(path, payload) {
      const controller = new AbortController();
      let timer;
      const operation = (async () => {
        const response = await fetchImpl(root + path, {
          method: payload ? 'POST' : 'GET',
          credentials: 'same-origin', redirect: 'error', cache: 'no-store',
          headers: payload ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
          ...(payload ? { body: JSON.stringify(payload) } : {}), signal: controller.signal
        });
        if (!response || typeof response.ok !== 'boolean' || typeof response.json !== 'function') fail('INVALID_RESPONSE', 'The cart response could not be read.');
        if (!response.ok) {
          let description = '';
          try { const data = await response.json(); description = typeof data?.description === 'string' ? data.description : ''; } catch (_) { /* Preserve HTTP failure. */ }
          fail('HTTP_ERROR', 'Shopify could not complete the cart request.', { status: response.status, description });
        }
        return response.json();
      })();
      const deadline = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          reject(new CartAdapterError('TIMEOUT', 'The cart request timed out; its result may be uncertain.'));
          controller.abort();
        }, timeoutMs);
      });
      try { return await Promise.race([operation, deadline]); }
      catch (error) {
        if (error instanceof CartAdapterError) throw error;
        throw new CartAdapterError('NETWORK_ERROR', 'The cart response could not be confirmed.');
      } finally { clearTimeout(timer); }
    }
    function checkAvailable() {
      if (!enabled) fail('DISABLED', 'Cart integration is disabled in this preview.');
      if (busy) fail('BUSY', 'A cart request is already in progress. Please wait.');
    }
    async function readAndVerify(recovered) {
      state = 'verifying';
      let cart;
      try { cart = await requestJson('cart.js'); }
      catch (error) {
        state = 'blocked';
        fail('CART_UNCONFIRMED', 'The addition may have completed. Do not add again. Use the read-only cart recheck.', { readError: error.code, postError: postError?.code });
      }
      try { verifyCart(cart, attempted); }
      catch (error) {
        state = 'blocked';
        const rejected = postError?.code === 'HTTP_ERROR' && postError.details.status >= 400 && postError.details.status < 500;
        fail(rejected ? 'ADD_REJECTED' : postError ? 'ADD_UNCONFIRMED' : error.code,
          rejected ? 'Shopify rejected the addition, possibly because of stock or availability. The cart may contain a partial addition. Review it before making changes.' : postError ? 'The addition is not confirmed. Do not add again; recheck or review the existing cart.' : error.message,
          { verificationCode: error.code, verificationDetails: error.details, postError: postError?.code, status: postError?.details.status, description: postError?.details.description, cart });
      }
      // A 4xx never enables checkout in the submission flow, even if read-back matches.
      if (!recovered && postError?.code === 'HTTP_ERROR' && postError.details.status >= 400 && postError.details.status < 500) {
        state = 'blocked';
        fail('ADD_REJECTED', 'Shopify reported an addition error. Use a read-only recheck before continuing.', { status: postError.details.status, cart });
      }
      state = 'verified';
      return freeze({ verified: true, cart, quote: attempted.quote, requestId: attempted.requestId, checkoutPath: root + 'checkout', reconciled: recovered || postError !== null });
    }
    async function submit(input) {
      checkAvailable();
      if (attempted) fail('RECOVERY_REQUIRED', 'An addition was already attempted. Do not add again; use the read-only cart recheck.');
      busy = true;
      try {
        const request = buildFromValidated(catalog, input, requestIdFactory(), productId, qaFixture);
        state = 'reading';
        const cart = await requestJson('cart.js');
        cartEnvelope(cart);
        if (cart.items.length || cart.item_count) {
          const wholesaleIds = new Set(catalog.map(record => record.variantId));
          const hasWholesale = cart.items.some(line => wholesaleIds.has(id(line.variant_id)) || line.properties?._pl8_wholesale || String(line.sku || '').startsWith('WS-'));
          fail(hasWholesale ? 'EXISTING_WHOLESALE' : 'CART_NOT_EMPTY', hasWholesale ? 'Wholesale items already exist in your cart. Review them there to avoid duplicate additions.' : 'Your cart already contains other items. Keep them and finish or review that cart before starting a wholesale order.');
        }
        if (cart.total_price !== 0 || cart.items_subtotal_price !== 0) fail('INVALID_CART_PRICE', 'The empty cart has unexpected or unavailable server totals.');
        // Once dispatch is possible, this instance will never issue another POST.
        attempted = request;
        state = 'adding';
        try { await requestJson('cart/add.js', request.payload); }
        catch (error) { postError = error; }
        return await readAndVerify(false);
      } catch (error) {
        state = attempted ? 'blocked' : 'idle';
        throw error;
      } finally { busy = false; }
    }
    async function reconcile() {
      checkAvailable();
      if (!attempted) fail('NO_ATTEMPT', 'There is no attempted addition to recheck.');
      busy = true;
      try { return await readAndVerify(true); }
      finally { busy = false; }
    }
    // No DOM listeners, storage, cart clearing, navigation, payment, or automatic retry.
    return Object.freeze({
      submit, reconcile,
      get status() { return Object.freeze({ state, busy, postAttempted: attempted !== null, canSubmit: enabled && !busy && !attempted, canCheckout: state === 'verified' && !busy }); }
    });
  }
  function createAdapter(options = {}) {
    return createAdapterWithCatalog(options, validateCatalog(options.catalog), PRODUCT_ID, false);
  }
  function createQaFixtureAdapter(options = {}) {
    return createAdapterWithCatalog(options, validateQaCatalog(options.catalog), QA_PRODUCT_ID, true);
  }
  return Object.freeze({ createAdapter, createQaFixtureAdapter, buildRequest, verifyCart, validateCatalog, validateQaCatalog, CartAdapterError, TIERS, JPY_SCALE, PRODUCT_ID, QA_PRODUCT_ID });
});
